import { InfiniteData, QueryKey, keepPreviousData } from '@tanstack/query-core';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';

import {
  CampaignItems,
  ClawbackEmailTemplate,
  ClawbackEmailTemplateListResponse,
  ClawbackEmailTemplatesRequestParams,
  CampaignsDraftInformation,
  CampaignsRequestParams,
  ExternalSystemsList,
  ListMetaCampaigns,
  ListMetaVendorDocuments,
  ListMetaVendors,
  POSendingSystem,
  POSendingSystemsList,
  SingleCampaignRequestParams,
  Vendor,
  VendorExternalSystemsList,
  VendorsRequestParams,
  VendorsSimplified,
} from 'common/api-types/vendor';
import { UseQueryConfig } from 'common/query/types';
import { AxiosVendorError, AxiosVendorIntegrationError } from 'common/types';

import {
  fetchVendorDocuments,
  fetchVendorDetails,
  fetchVendors,
  fetchVendorExternalSystems,
  fetchExternalSystems,
  getCampaigns,
  getSingleCampaign,
  fetchSimplifiedVendors,
  fetchVendorPOSendingSystem,
  fetchPOSendingSystems,
  fetchVendorDistributors,
  getVendorBaseTerms,
  getBaseTerm,
  getVendorFundingBudgets,
  getVendorFundingBudget,
  getCampaignsDraftInformation,
  getVendorProductGroupings,
  fetchClawbackEmailTemplates,
  fetchClawbackEmailTemplate,
} from './api';

export const VENDORS_QUERY_KEY_ROOT = 'vendors';
export const VENDOR_DETAILS_QUERY_KEY_ROOT = 'vendorDetails';
export const VENDOR_DOCUMENTS_QUERY_KEY_ROOT = 'vendorDocuments';
export const EXTERNAL_SYSTEMS_QUERY_KEY_ROOT = 'externalSystems';
export const VENDOR_FUNDINGS_QUERY_KEY_ROOT = 'vendorFundings';
const VENDORS_SIMPLIFIED_QUERY_KEY_ROOT = 'vendorsSimplified';
export const PO_SENDING_SYSTEMS_QUERY_KEY_ROOT = 'poSendingSystems';
export const BASE_TERMS_QUERY_KEY_ROOT = 'baseTerms';
export const FUNDING_BUDGETS_QUERY_KEY_ROOT = 'fundingBudgets';
const CAMPAIGNS_DRAFT_INFORMATION_QUERY_KEY_ROOT = 'campaignsDraftInformation';
export const CLAWBACK_EMAIL_TEMPLATES_QUERY_KEY_ROOT = 'clawbackEmailTemplates';

export function useVendorsQuery(params: VendorsRequestParams, enabled?: boolean) {
  return useQuery<ListMetaVendors, AxiosVendorError>({
    queryFn: () => fetchVendors(params),
    queryKey: [VENDORS_QUERY_KEY_ROOT, params],
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useSimplifiedVendorsQuery(
  params: { vendorIds: string[] },
  enabled: boolean = false,
) {
  return useQuery<VendorsSimplified, AxiosVendorError>({
    queryFn: () => fetchSimplifiedVendors({ ids: params.vendorIds }),
    queryKey: [VENDORS_SIMPLIFIED_QUERY_KEY_ROOT, params],
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useVendorDetailsQuery(id: string) {
  return useQuery<Vendor, AxiosVendorError>({
    queryFn: () => fetchVendorDetails(id),
    queryKey: [VENDOR_DETAILS_QUERY_KEY_ROOT, { id }],
    placeholderData: keepPreviousData,
    staleTime: 300_000,
  });
}

export function useVendorDistributorsQuery(id: string) {
  return useQuery({
    queryFn: () => fetchVendorDistributors(id),
    queryKey: [VENDOR_DETAILS_QUERY_KEY_ROOT, id, 'distributors'],
    select: (data) => data.vendors,
  });
}

export function useVendorDocumentsQuery(id: Vendor['id']) {
  return useQuery<ListMetaVendorDocuments, AxiosVendorError>({
    queryFn: () => fetchVendorDocuments(id),
    queryKey: [VENDOR_DOCUMENTS_QUERY_KEY_ROOT, { id }],
    placeholderData: keepPreviousData,
  });
}

export function useExternalSystemsQuery(
  options: UseQueryConfig<ExternalSystemsList, AxiosVendorIntegrationError> = {},
) {
  return useQuery<ExternalSystemsList, AxiosVendorIntegrationError>({
    queryFn: () => fetchExternalSystems(),
    queryKey: [EXTERNAL_SYSTEMS_QUERY_KEY_ROOT],
    ...options,
  });
}

export function useVendorExternalSystemsQuery(id: Vendor['id']) {
  return useQuery<VendorExternalSystemsList, AxiosVendorIntegrationError>({
    queryFn: () => fetchVendorExternalSystems(id),
    queryKey: [EXTERNAL_SYSTEMS_QUERY_KEY_ROOT, { id }],
  });
}

export function useBaseTermsQuery(vendorId: string) {
  return useQuery({
    queryFn: () => getVendorBaseTerms(vendorId),
    queryKey: [BASE_TERMS_QUERY_KEY_ROOT, vendorId],
    placeholderData: keepPreviousData,
  });
}

export function useBaseTermQuery(vendorId: string, termId: string, enabled: boolean) {
  return useQuery({
    queryFn: () => getBaseTerm(vendorId, termId),
    queryKey: [BASE_TERMS_QUERY_KEY_ROOT, vendorId, termId],
    enabled,
  });
}

export function useVendorFundingBudgetsQuery(vendorId: string) {
  return useQuery({
    queryFn: () => getVendorFundingBudgets(vendorId),
    queryKey: [FUNDING_BUDGETS_QUERY_KEY_ROOT, { vendorId }],
    placeholderData: keepPreviousData,
  });
}

export function useVendorFundingBudgetQuery(vendorId: string, budgetId: string, enabled: boolean) {
  return useQuery({
    queryFn: () => getVendorFundingBudget(vendorId, budgetId),
    queryKey: [FUNDING_BUDGETS_QUERY_KEY_ROOT, { vendorId, budgetId }],
    enabled,
  });
}

export function useVendorProductGroupingsQuery(vendorId: string, enabled: boolean) {
  return useQuery({
    queryFn: () => getVendorProductGroupings(vendorId),
    queryKey: [BASE_TERMS_QUERY_KEY_ROOT, 'productGroupings', vendorId],
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useCampaignsQuery(params: CampaignsRequestParams) {
  return useInfiniteQuery<
    ListMetaCampaigns,
    AxiosVendorError,
    InfiniteData<ListMetaCampaigns>,
    QueryKey,
    { page: number }
  >({
    enabled: true,
    queryFn: ({ signal, pageParam }) => {
      return getCampaigns(
        {
          ...params,
          page: pageParam.page,
          pageSize: 20,
        },
        signal,
      );
    },
    queryKey: [VENDOR_FUNDINGS_QUERY_KEY_ROOT, 'campaigns', params],
    getNextPageParam: (data) => {
      if (!data) {
        return { page: 0 };
      }
      if (data.meta.currentPage !== data.meta.totalPages) {
        return { page: data.meta.currentPage + 1 };
      }
      return undefined;
    },
    initialPageParam: { page: 1 },
  });
}

export function useSingleCampaignQuery(params: SingleCampaignRequestParams) {
  return useQuery<CampaignItems, AxiosVendorError>({
    queryFn: () => getSingleCampaign(params),
    queryKey: [VENDOR_FUNDINGS_QUERY_KEY_ROOT, params],
    placeholderData: keepPreviousData,
  });
}

export function useVendorPOSendingSystemQuery(id: Vendor['id']) {
  return useQuery<POSendingSystem, AxiosVendorIntegrationError>({
    queryFn: () => fetchVendorPOSendingSystem(id),
    queryKey: [PO_SENDING_SYSTEMS_QUERY_KEY_ROOT, { id }],
  });
}

export function usePOSendingSystemsQuery() {
  return useQuery<{ systemList: POSendingSystemsList }, AxiosVendorIntegrationError>({
    queryFn: () => fetchPOSendingSystems(),
    queryKey: [PO_SENDING_SYSTEMS_QUERY_KEY_ROOT],
  });
}

export function useCampaignsDraftInformationQuery(draftId: CampaignsDraftInformation['id']) {
  return useQuery({
    queryFn: () => getCampaignsDraftInformation(draftId),
    queryKey: [CAMPAIGNS_DRAFT_INFORMATION_QUERY_KEY_ROOT, { draftId }],
  });
}

export function useClawbackEmailTemplatesQuery(
  params?: ClawbackEmailTemplatesRequestParams,
  options: UseQueryConfig<ClawbackEmailTemplateListResponse, AxiosVendorError> = {},
) {
  return useQuery({
    queryFn: () => fetchClawbackEmailTemplates(params),
    queryKey: [CLAWBACK_EMAIL_TEMPLATES_QUERY_KEY_ROOT, 'list', params],
    placeholderData: keepPreviousData,
    ...options,
  });
}

/** Vendor communication route uses this search value for "create"; it is not a real template id. */
const CLAWBACK_EMAIL_TEMPLATE_CREATE_SEARCH_ID = 'new';

export function useClawbackEmailTemplateQuery(
  templateId: ClawbackEmailTemplate['id'],
  options: UseQueryConfig<ClawbackEmailTemplate, AxiosVendorError> = {},
) {
  const { enabled: enabledOption, ...rest } = options;
  const isFetchableTemplateId =
    Boolean(templateId) && templateId !== CLAWBACK_EMAIL_TEMPLATE_CREATE_SEARCH_ID;
  return useQuery({
    queryFn: () => fetchClawbackEmailTemplate(templateId),
    queryKey: [CLAWBACK_EMAIL_TEMPLATES_QUERY_KEY_ROOT, 'detail', templateId],
    placeholderData: keepPreviousData,
    ...rest,
    enabled: isFetchableTemplateId && (enabledOption ?? true),
  });
}
